// @flow
import { Map, Record } from "immutable"

import { SPORTS_EQUIPMENT_SUBMIT_FAILURE } from "mmb/scenes/AccountBooking/components/Booking/scenes/BookingAdditionalServices/scenes/SportsEquipment/consts/actions"
import { SPECIAL_ASSISTANCE_SUBMIT_FAILURE } from "mmb/scenes/AccountBooking/components/Booking/scenes/BookingAdditionalServices/scenes/SpecialAssistance/consts/actions"
import { PETS_SUBMIT_FAILURE } from "mmb/scenes/AccountBooking/components/Booking/scenes/BookingAdditionalServices/scenes/Pets/consts/actions"
import { MUSICAL_EQUIPMENT_SUBMIT_FAILURE } from "mmb/scenes/AccountBooking/components/Booking/scenes/BookingAdditionalServices/scenes/MusicalEquipment/consts/actions"
import getPaymentConfig, {
  getUnsupportedCardTypes,
} from "common/services/payments/getPaymentConfig"
import { getLanguageInfo } from "common/services/staticData"
import type { BookingId } from "mmb/types/primitiveTypes"
import { mapperCreatedBookingChange } from "./mapper/services/mapperManageBookingChange"
import cuckoo from "common/services/cuckoo/cuckoo"
import { cuckooEvents } from "common/consts/enums"
import * as bookingsApi from "mmb/services/bookings/bookingsApi"
import * as manageApi from "./api/manageApi"
import { ERROR, LOADING, SUCCESS } from "common/consts/phase"
import type { Modals } from "mmb/consts/booking"
import { ACTIONS, BOOKING_STATUSES, CONTEXTS, MODALS } from "mmb/consts/booking"
import { fetchMealsOptions } from "../scenes/BookingMeals/services/api"
import { currencySelector } from "common/services/currencies/selectors"
import { LOGOUT_REQUEST } from "common/services/user/index"
import { bookedAtSelector } from "common/services/branding/selectors"
import { bidSelector } from "./selector"
import {
  createdAdditionalServiceToPaySelectorNonMemoized as createdAdditionalServiceToPaySelector,
  getAdditionalServiceToPaySelector,
} from "mmb/scenes/AccountBooking/components/Booking/scenes/AdditionalServices/services/selectors/additionalServicesSelectors"
import {
  additionalServicePaymentPayloadSelector,
  bookingPaymentSelector,
  itemToPaySelector,
  paymentSummarySelector,
} from "../scenes/BookingPayment/services/paymentSelector"
import { SUBMIT_MEALS_ORDER_FAILURE } from "../scenes/BookingMeals/constants"
// eslint-disable-next-line import/no-cycle
import { changeView, chooseFlightToExchange, scheduleChangeJourney } from "./scheduleChanges"
import type {
  Action,
  BookingActionAction,
  BookingClearErrorAction,
  BookingClearSuccessAction,
  BookingInvalidateEndAction,
  BookingInvalidateStartAction,
  BookingLoadSeatsAction,
  BookingResetAction,
  BookingSetModalOpenAction,
  BookingStateInst,
  BookingToggleTimelineAction,
  ChangePrimaryEmail,
  GetState,
  ServiceForPaymentType,
} from "./types"
import type { FormData } from "../scenes/BookingPayment/services/validateBookingPayment"
import {
  additionalServicePayment as additionalServicePaymentApi,
  requestAdditionalServicePayment,
} from "../scenes/BookingPayment/services/paymentApi"
// eslint-disable-next-line import/no-cycle
import {
  additionalServiceOrderSuccess,
  returnToOrderingStage,
} from "../scenes/BookingPayment/services/paymentActions"
import { shouldLoadScheduleChange } from "./scheduleChanges/services/helpers"
import {
  isABTestSet,
  isActionRepair,
  isLive,
  splitsterSHOTABtestVelueSelector,
} from "booking/services/reservation/helpers/scheduleChangeHelper"
import { mapperMealOptions } from "../scenes/BookingMeals/services/helpers/mapperMealOptions"
import { BookingDataClass } from "mmb/externals"
import { SERVICES } from "mmb/consts/additionalServices"
import { SUBMIT_ORDER_CORRECTION_FAILURE } from "mmb/scenes/AccountBooking/components/Booking/scenes/BookingPassengerEdit/services/consts"
import {
  ADDITIONAL_SERVICE_ORDER_SUCCESS,
  SUBMIT_FOR_PAYMENT,
} from "mmb/scenes/AccountBooking/components/Booking/scenes/BookingPayment/records/consts"
import { ACTIONS as SHOT_ACTIONS } from "mmb/scenes/AccountBooking/components/Booking/services/scheduleChanges/services/consts"
import {
  ACTION,
  ACTION_FAILURE,
  ACTION_SUCCESS,
  CHANGE_VIEW,
  CLEAR_ERROR,
  CLEAR_SUCCESS,
  INVALIDATE_END,
  INVALIDATE_START,
  LOAD,
  LOAD_FAILURE,
  LOAD_REQUEST,
  LOAD_SEATS,
  LOAD_SEATS_FAILURE,
  LOAD_SEATS_SUCCESS,
  LOAD_SUCCESS,
  RELOAD,
  RELOAD_FAILURE,
  RELOAD_SUCCESS,
  RESET,
  SET_MODAL_OPEN,
  TOGGLE_TIMELINE,
} from "mmb/scenes/AccountBooking/components/Booking/services/consts"
import { SUBMIT_SELECTED_FLIGHT_FAILURE } from "mmb/scenes/AccountBooking/components/Booking/scenes/BookingPassengerAta/consts/constants"
import { SUBMIT_SEATS_FAILURE } from "mmb/scenes/AccountBooking/components/Booking/services/additionalServices/consts"
import { SUBMIT_FAILURE as INSURANCE_SUBMIT_FAILURE } from "mmb/scenes/AccountBooking/components/Booking/services/bookingInsurance/consts"
import { CONFIRM_FAILURE as FLIGHT_CHANGE_SUBMIT_FAILURE } from "mmb/scenes/AccountBooking/components/Booking/services/changeFlights/consts"
import { LOAD_MEALS } from "mmb/scenes/AccountBooking/components/Booking/scenes/BookingMeals/services/mealConsts"
import { trackOrderError } from "../scenes/BookingAddBags/services/helpers"

// README:
// actions have 'payload' on them because of this.
// see 'bookingSelectors' for usage
const Message = new Record({
  action: null,
  payload: null,
  error: null,
})

const BookingState = new Record({
  data: null,
  phase: LOADING,
  invalidating: false,
  error: null,
  success: null,
  modal: null,
  showTimeline: false,
  previousModal: null,
})

export default function bookingReducer(
  state: Map<string, BookingStateInst> = new BookingState(),
  raw: Action,
) {
  if (!(state instanceof BookingState)) return new BookingState()

  const action = raw
  if (action.bid) {
    // Maps recognize between numbers and strings
    // $FlowFixMe
    action.bid = Number(action.bid)
  }

  switch (action.type) {
    case LOGOUT_REQUEST:
      return new Map()

    case CHANGE_VIEW:
      return state.set("view", action.view)

    case SUBMIT_FOR_PAYMENT:
    case SET_MODAL_OPEN:
      return state.set("modal", action.modal).set("previousModal", state.get("modal"))

    case TOGGLE_TIMELINE:
      return state.set("showTimeline", !state.get("showTimeline"))

    case CLEAR_ERROR:
      return state.set("error", null)

    case CLEAR_SUCCESS:
      return state.set("success", null)

    // load
    // ---
    case LOAD_REQUEST:
      return state.set("phase", LOADING)

    case LOAD_SUCCESS:
      return state.set("phase", SUCCESS).set("data", action.res)

    case LOAD_FAILURE:
      return state.set("phase", ERROR).set(
        "error",
        new Message({
          error: action.error,
          payload: action.type,
        }),
      )

    case LOAD_SEATS_SUCCESS: {
      const data = state.get("data")
      const newData = new BookingDataClass({ ...data, seatmaps: action.res })

      return state.set("data", newData)
    }

    case SHOT_ACTIONS.LOAD_NO_RESET_SUCCESS:
    case SHOT_ACTIONS.LOAD_SCHEDULE_CHANGE_SUCCESS: {
      const data = state.get("data")
      data.scheduleChange = action.res
      data.journey = scheduleChangeJourney(data.journey, action.res)

      return state.set("data", data)
    }

    // reload
    // ---

    case RELOAD_SUCCESS:
      return state.set("data", action.res)

    case RELOAD_FAILURE:
      return state.set(
        "error",
        new Message({
          action: ACTIONS.INVALIDATE,
          error: action.error,
        }),
      )

    // reset || unload
    // ---

    case RESET:
      return state.clear()

    // action
    // ---
    case ACTION_SUCCESS:
      return state.set("success", new Message(action))

    case ACTION_FAILURE:
    case SPORTS_EQUIPMENT_SUBMIT_FAILURE:
    case SPECIAL_ASSISTANCE_SUBMIT_FAILURE:
    case PETS_SUBMIT_FAILURE:
    case MUSICAL_EQUIPMENT_SUBMIT_FAILURE:
    case FLIGHT_CHANGE_SUBMIT_FAILURE:
    case SUBMIT_SEATS_FAILURE:
    case INSURANCE_SUBMIT_FAILURE:
    case SUBMIT_ORDER_CORRECTION_FAILURE:
    case SHOT_ACTIONS.REQUEST_RESOLUTION_FAILURE:
    case SUBMIT_MEALS_ORDER_FAILURE:
    case SUBMIT_SELECTED_FLIGHT_FAILURE:
      return state.set("error", new Message(action))

    case SHOT_ACTIONS.LOAD_FAILURE:
    case LOAD_SEATS_FAILURE:
      return state.set("error", action)

    // invalidate
    // ---

    case INVALIDATE_START:
      return state.set("invalidating", true)

    case INVALIDATE_END:
      return state.set("invalidating", false)
    case ADDITIONAL_SERVICE_ORDER_SUCCESS: {
      const { additionalService } = action.payload
      const booking = state.get("data")
      booking.additionalServices = booking.additionalServices.concat([additionalService])
      return state.set("data", booking)
    }
    default:
      return state
  }
}

// exported
// ---

export const openModal = (modal: Modals): BookingSetModalOpenAction => ({
  type: SET_MODAL_OPEN,
  modal,
  _track: { modal },
})

export const toggleTimeline = (): BookingToggleTimelineAction => ({
  type: TOGGLE_TIMELINE,
})

export const closeModal = (): BookingSetModalOpenAction => ({
  type: SET_MODAL_OPEN,
  modal: null,
})

export const clearErrorAction = (): BookingClearErrorAction => ({
  type: CLEAR_ERROR,
})

export const clearSuccessAction = (): BookingClearSuccessAction => ({
  type: CLEAR_SUCCESS,
})

const loadSeats = (bid: BookingId): BookingLoadSeatsAction => ({
  type: LOAD_SEATS,
  promise: bookingsApi.findSeats(bid),
  bid,
  _track: { bid },
})

const loadMealsAction = (bid: number) => ({
  type: LOAD_MEALS,
  promise: fetchMealsOptions(bid).then(res => mapperMealOptions(res.segments)),
  bid,
  _track: { bid },
})

export const loadScheduleChangeAction = (
  bid: BookingId,
  retry?: number = 25,
  retryTimeout?: number = 5000,
  shouldRetry?: boolean,
  shouldOpenModal?: boolean = true,
  // $FlowFixMe
) => async dispatch => {
  let promise = bookingsApi.fetchScheduleChange(bid, shouldOpenModal ? 10 : 0)
  promise
    .then(scheduleChanges => {
      if (shouldLoadScheduleChange(scheduleChanges, retry, shouldRetry)) {
        dispatch({ type: SHOT_ACTIONS.SET_VIEW_ALTERNATIVES, payload: { view: true } })
        setTimeout(
          () =>
            dispatch(
              loadScheduleChangeAction(bid, retry - 1, retryTimeout + 1000, shouldRetry, false),
            ),
          retryTimeout,
        )
      } else {
        dispatch({ type: SHOT_ACTIONS.SET_VIEW_ALTERNATIVES, payload: { view: false } })
        dispatch(changeView("alternative", 1))
        // $FlowFixMe
        dispatch(chooseFlightToExchange(111)) // magic number
      }
    })
    .catch(err => {
      dispatch(openModal(MODALS.SCHEDULE_CHANGE_ALTERNATIVE_ERROR))
      return err
    })

  if (retry < 0) {
    dispatch(openModal(MODALS.SCHEDULE_CHANGE_ALTERNATIVE_ERROR))
    promise = Promise.reject("scheduleChange/timeout")
  }

  const ok = await dispatch({
    type: !shouldRetry ? SHOT_ACTIONS.LOAD_SCHEDULE_CHANGE : SHOT_ACTIONS.LOAD_NO_RESET,
    promise,
    bid,
    _track: { bid },
  })

  promise
    .then(scheduleChanges => {
      if (
        scheduleChanges.get("alternatives").size === 0 &&
        shouldRetry && // maybe delete
        scheduleChanges.get("flightsChecked")
      ) {
        dispatch(openModal(MODALS.SCHEDULE_CHANGE_ALTERNATIVE_ERROR))
      } else if (ok && (scheduleChanges.get("flightsChecked") || shouldOpenModal)) {
        dispatch(openModal(MODALS.SCHEDULE_CHANGE))
      }
    })
    .catch(console.log)

  return Promise.resolve()
}

const loadAncillaries = (dispatch, bid) => {
  dispatch(loadSeats(bid))
  dispatch(loadMealsAction(bid))
}

const awaitScheduleChanges = async (dispatch, bid) => {
  dispatch({ type: INVALIDATE_START, bid })
  await dispatch(loadScheduleChangeAction(bid))
  dispatch({ type: INVALIDATE_END, bid })
}

const loadBookingFactory = type => (bid: BookingId) => async (
  dispatch: Dispatch<*>,
  getState: GetState,
  // eslint-disable-next-line consistent-return
) => {
  const state = getState()
  const languageInfo = getLanguageInfo(state)
  const promise = bookingsApi.findOne(bid, languageInfo.api)
  const abTestValue = splitsterSHOTABtestVelueSelector(state)
  try {
    const ok = await dispatch({
      type,
      promise,
      bid,
      _track: { bid },
    })

    if (ok) {
      const booking = await promise
      const isConfirmed = booking.status === BOOKING_STATUSES.CONFIRMED

      const shouldLoadScheduleChanges =
        !isActionRepair(booking.scheduleChange) &&
        (isLive(booking.scheduleChange) || isABTestSet(booking.scheduleChange, abTestValue))

      await Promise.all([
        isConfirmed && loadAncillaries(dispatch, booking.id),
        shouldLoadScheduleChanges && awaitScheduleChanges(dispatch, booking.id),
      ])
    }
  } catch (err) {
    return err
  }
}

const invalidateBooking = loadBookingFactory(RELOAD)
export const loadBooking = loadBookingFactory(LOAD)

// jest breaks on syntax error otherwise (prettier)
// eslint-disable-next-line arrow-body-style
export const reloadBooking = (bid: BookingId, showLoader?: boolean = true) => {
  return async (dispatch: Dispatch<*>) => {
    if (showLoader) {
      dispatch(({ type: INVALIDATE_START }: BookingInvalidateStartAction))
    }
    await dispatch(invalidateBooking(bid))
    if (showLoader) {
      dispatch(({ type: INVALIDATE_END }: BookingInvalidateEndAction))
    }
  }
}

// $FlowFixMe flow doesnt support redux thunk for now
export const reloadBookingAndCloseModal = () => (dispatch: Dispatch<*>, getState: GetState) => {
  const bid = bidSelector(getState())
  dispatch(reloadBooking(bid))
  dispatch(closeModal())
}

export const resetBooking = () => async (dispatch: Dispatch<*>) => {
  dispatch(({ type: RESET }: BookingResetAction))
}

// Booking services
// ---
export const requestFastRefund = (bid: BookingId) => async (dispatch: Dispatch<*>) => {
  dispatch(({ type: INVALIDATE_START }: BookingInvalidateStartAction))
  const ok = await dispatch(
    ({
      type: ACTION,
      action: ACTIONS.REQUEST_FAST_REFUND,
      promise: manageApi.requestFastRefund(bid),
      bid,
      payload: { bid },
      _track: { action: ACTIONS.REQUEST_FAST_REFUND, bid },
    }: BookingActionAction),
  )

  await dispatch(invalidateBooking(bid))
  dispatch(({ type: INVALIDATE_END }: BookingInvalidateEndAction))
  dispatch(closeModal())
  return ok
}

export const requestPendingRefund = (payload: { bid: BookingId }) => async (
  dispatch: Dispatch<*>,
) => {
  const ok = await dispatch(
    ({
      type: ACTION,
      action: ACTIONS.REQUEST_PENDING_REFUND,
      promise: manageApi.requestPendingRefund(payload),
      bid: payload.bid,
      payload,
      _track: { action: ACTIONS.REQUEST_PENDING_REFUND, payload },
    }: BookingActionAction),
  )

  return ok
}

export const changeBags = (serviceForPayment: ServiceForPaymentType) => async (
  dispatch: Dispatch<*>,
  getState: GetState,
) => {
  const state = getState()

  const { payload } = serviceForPayment

  const promise = manageApi.changeBags({
    ...payload,
    currency: currencySelector(state),
    booked_at: bookedAtSelector(state),
  })

  const ok = await dispatch(
    ({
      type: ACTION,
      action: ACTIONS.CHANGE_BAGS,
      promise,
      bid: serviceForPayment.bid,
      payload,
      _track: { action: ACTIONS.CHANGE_BAGS, payload },
    }: BookingActionAction),
  )

  if (ok) {
    cuckoo.track(cuckooEvents.MMB_BAGGAGE_ORDERED, {
      price: payload.price,
      currencyShown: serviceForPayment.currency,
      priceShown: serviceForPayment.price,
    })

    if (serviceForPayment.price <= 0) {
      dispatch(openModal(MODALS.CREDIT_PAYMENT_THANK_YOU))

      return ok
    }
    return promise.then(res => res.detail).then(mapperCreatedBookingChange)
  }

  return ok
}

export const changeBaggage = (serviceForPayment: ServiceForPaymentType) => async (
  dispatch: Dispatch<*>,
  getState: GetState,
) => {
  const bid = bidSelector(getState())

  const { payload } = serviceForPayment

  const payment = {
    paid_by_card: {
      gate: "zooz",
      price: {
        amount: payload.useBalance ? "0" : String(serviceForPayment.price),
        currency: serviceForPayment.currency.toUpperCase(),
      },
    },
    paid_by_credits: {
      price: {
        amount: payload.useBalance ? String(serviceForPayment.price) : "0",
        currency: serviceForPayment.currency.toUpperCase(),
      },
    },
  }

  const promise = manageApi.changeBaggage({
    bid,
    payload: { requested_combinations: payload.requested_combinations, payment },
  })

  const ok = await dispatch(
    ({
      type: ACTION,
      action: ACTIONS.CHANGE_BAGS,
      promise,
      bid: serviceForPayment.bid,
      payload,
      _track: { action: ACTIONS.CHANGE_BAGS, payload, payment },
    }: BookingActionAction),
  )

  if (ok) {
    cuckoo.track(cuckooEvents.MMB_BAGGAGE_ORDERED, {
      price: payload.price,
      currencyShown: serviceForPayment.currency,
      priceShown: serviceForPayment.price,
    })

    if (serviceForPayment.price <= 0) {
      dispatch(openModal(MODALS.CREDIT_PAYMENT_THANK_YOU))

      return ok
    }
    return promise.then(res => res).then(mapperCreatedBookingChange)
  }
  promise.catch(trackOrderError)

  return ok
}

// $FlowFixMe
export const finishCreditPaymentAction = service => async (
  dispatch: Dispatch<*>,
  getState: GetState,
) => {
  const state = getState()
  const bid = bidSelector(state)
  const bookedAt = bookedAtSelector(state)
  const { serviceIdForPayment } = bookingPaymentSelector(state)
  const serviceToPay = getAdditionalServiceToPaySelector(state)(serviceIdForPayment) || service
  const id = serviceToPay?.id
  const category = serviceToPay?.category

  const payload = {
    bid,
    abid: id,
  }

  const ok = await dispatch({
    type: ACTION,
    action: ACTIONS.ADDITIONAL_SERVICE_PAYMENT,
    promise: requestAdditionalServicePayment({
      ...payload,
      booked_at: bookedAt,
      category,
    }),
    bid,
    payload,
    _track: { action: ACTIONS.ADDITIONAL_SERVICE_PAYMENT, payload },
  })

  if (ok) {
    cuckoo.track(cuckooEvents.MMB_PAYMENT_DONE, {
      method: "credit",
      category,
    })
  }

  return ok
}

const isExistingRequestAndPayingWithCredits = (serviceForPayment, formValues) =>
  !serviceForPayment.category && formValues.payWithCredits

const isNewRequestAndPayingWithCredits = (serviceForPayment, formValues) =>
  serviceForPayment.category && formValues.payWithCredits

const shouldCreateNewRequest = (serviceForPayment, state) =>
  serviceForPayment.category && !createdAdditionalServiceToPaySelector(state)

const hasRequestFailed = (serviceForPayment, props) =>
  serviceForPayment.category && props?.additionalService === false

const setAsCreditPayment = serviceForPayment => ({
  ...serviceForPayment,
  payload: {
    useBalance: true,
    ...serviceForPayment.payload,
  },
})

// For credit payment, certain services require requestPayment to finish
const requiresPaymentRequestToClose = (category: string) => [SERVICES.SEATING].includes(category)

// $FlowFixMe flow doesnt support redux thunk for now
export const additionalServicePayment = (formData: FormData) => async (
  dispatch: Dispatch<*>,
  getState: GetState,
) => {
  const state = getState()
  const serviceForPayment = itemToPaySelector(state)

  if (isExistingRequestAndPayingWithCredits(serviceForPayment, formData)) {
    return dispatch(finishCreditPaymentAction(serviceForPayment))
  }

  if (isNewRequestAndPayingWithCredits(serviceForPayment, formData)) {
    const service = await dispatch(
      serviceForPayment.orderAction(setAsCreditPayment(serviceForPayment)),
    )

    if (service) {
      if (requiresPaymentRequestToClose(service.category)) {
        // Send request payment action. This will finish credit payment for other services
        dispatch(additionalServiceOrderSuccess(service))
        return dispatch(finishCreditPaymentAction(service))
      }
      // Finished credit payment for certain services. Display success message in modal
      return dispatch({
        type: ACTION_SUCCESS,
        action: ACTIONS.ADDITIONAL_SERVICE_PAYMENT,
        payload: { additionalService: service },
      })
    }
    return false
  }

  const props = shouldCreateNewRequest(serviceForPayment, state)
    ? {
        additionalService: await dispatch(serviceForPayment.orderAction(serviceForPayment)),
      }
    : null

  if (hasRequestFailed(serviceForPayment, props)) {
    return false
  }

  if (props && props.additionalService) {
    // Temporarily store ordered Additional service
    dispatch(additionalServiceOrderSuccess(props.additionalService))
  }

  const { payload, trackablePayload, additionalService } = additionalServicePaymentPayloadSelector(
    state,
    { ...props, formData },
  )

  const ok = await dispatch({
    type: ACTION,
    action: ACTIONS.ADDITIONAL_SERVICE_PAYMENT,
    promise: additionalServicePaymentApi(payload),
    bid: bidSelector(state),
    payload: { additionalService },
    _track: { action: ACTIONS.ADDITIONAL_SERVICE_PAYMENT, payload: trackablePayload },
  })

  if (ok) {
    cuckoo.track(cuckooEvents.MMB_PAYMENT_DONE, {
      method: "card",
      isNativePayment: Boolean(formData.isNativePayment),
      isNativePaymentSupported: Boolean(window.PaymentRequest),
      category: additionalService.category,
    })
  }

  return ok
}

// $FlowFixMe flow doesnt support redux thunk for now (coppied comment from above)
export const processNativePayment = () => (dispatch: Dispatch<*>, getState: GetState) => {
  const { summary } = paymentSummarySelector(getState())

  const {
    payments: {
      card: { card_types },
    },
  } = getPaymentConfig()
  const supportedCards = Object.keys(card_types).filter(
    cardType => !getUnsupportedCardTypes().includes(cardType),
  )

  // Supported payment methods
  const supportedInstruments = [
    {
      supportedMethods: ["basic-card"],
      data: {
        supportedNetworks: supportedCards,
      },
    },
  ]

  // $FlowFixMe
  const request = new PaymentRequest(supportedInstruments, summary)
  request
    .show()
    .then(async result => {
      const card = {
        number: result.details.cardNumber,
        holderName: result.details.cardholderName,
        expiry: {
          year: result.details.expiryYear,
          month: result.details.expiryMonth,
        },
        securityCode: result.details.cardSecurityCode,
      }

      await dispatch(additionalServicePayment({ card, isNativePayment: true }))

      result.complete("success")
    })
    .catch(() => {
      dispatch(returnToOrderingStage(dispatch, getState()))
    })
}

// $FlowFixMe flow doesnt support redux thunk for now
export const returnToPaymentAction = isNativePayment => (dispatch: Dispatch<*>) => {
  if (isNativePayment) {
    dispatch(processNativePayment())
  }
  dispatch(clearErrorAction())
}

export const updateContacts = (
  payload: Object, // eslint-disable-line flowtype/no-weak-types
  context?: string = CONTEXTS.MMB,
  invalidate?: boolean = true,
) => async (dispatch: Dispatch<*>) => {
  if (invalidate) {
    dispatch(({ type: INVALIDATE_START }: BookingInvalidateStartAction))
  }

  const promises = []

  if (payload.created.length) {
    promises.push(bookingsApi.submitContact(payload.bid, payload.created))
  }

  if (payload.removed.length) {
    payload.removed.forEach(item => {
      promises.push(bookingsApi.deleteContact(payload.bid, item.id))
    })
  }

  const promise = Promise.all(promises)

  const ok = await dispatch(
    ({
      type: ACTION,
      action: ACTIONS.ADDITIONAL_CONTACTS,
      promise,
      bid: payload.bid,
      payload,
      _track: { action: ACTIONS.ADDITIONAL_CONTACTS, payload, context },
    }: BookingActionAction),
  )

  await dispatch(invalidateBooking(payload.bid))
  dispatch(({ type: INVALIDATE_END }: BookingInvalidateEndAction))
  return ok
}

export const changePrimaryEmail = (data: ChangePrimaryEmail) => async (dispatch: Dispatch<*>) => {
  const ok = await dispatch({
    type: ACTION,
    action: ACTIONS.CHANGE_PRIMARY_EMAIL,
    promise: bookingsApi.changePrimaryEmail(data.bid, data.payload),
    bid: data.bid,
    payload: data.payload,
    _track: { action: ACTIONS.CHANGE_PRIMARY_EMAIL, data },
  })

  return ok
}
